import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, getAuthHeader } from './leadsConstants';

export function EmailModal({ lead, onClose }) {
  const [templates, setTemplates] = React.useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState('');
  const [emailData, setEmailData] = React.useState({ subject: '', body: '' });
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);

  React.useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/templates?template_type=email`, getAuthHeader());
      setTemplates(response.data);
      if (response.data.length > 0) {
        setSelectedTemplateId(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load templates');
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (selectedTemplateId && lead.id) {
      loadTemplate();
    }
  }, [selectedTemplateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplate = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/${lead.id}/render-template/${selectedTemplateId}`, getAuthHeader());
      setEmailData({
        subject: response.data.subject,
        body: response.data.body
      });
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const sendEmail = async () => {
    if (!lead.email) {
      toast.error('Lead has no email address');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads/send-email`, {
        to_email: lead.email,
        subject: emailData.subject,
        body: emailData.body,
        lead_id: lead.id
      }, getAuthHeader());
      toast.success('Email sent successfully');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-800 mb-4">
          Send Email to {lead.first_name} {lead.last_name}
        </h3>
        <p className="text-sm text-gray-500 mb-4">{lead.email}</p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
          <select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="h-32 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={emailData.subject}
                onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
              <textarea
                value={emailData.body}
                onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                rows={10}
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={sendEmail}
            disabled={sending || loading}
            className="px-4 py-2 text-white rounded-xl font-medium shadow-[0_4px_12px_rgba(5,150,105,0.30)] hover:shadow-[0_8px_20px_rgba(5,150,105,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100"
            style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
